import UnitNavMl from "./UnitNavMl";
import { useContext, useState } from "react";

import UnitCurrentContent from "./UnitCurrentContent";
import { UnitContext } from "../../contexts/contextExporter";
import { Link } from "react-router";
import { useAuth } from "../../hooks/useAuth";
import { useDateParts } from "../../hooks/useDateParts";
import { DEMO_COHORT_IDS } from "../../utils/static";
import Attendees from "../../components/Attendees";
import NoteButton from "../notes/NoteButton";
import { List, X, ArrowRight } from "@phosphor-icons/react";
import clsx from "clsx";
import LoadingScreen from "../../components/LoadingScreen";

export default function UnitMain({
  children,
}: {
  children: React.ReactNode;
}) {
  const { unit, pin, next, currentPart, canNavigate, isLive } =
    useContext(UnitContext);
  const { user, isInstructor, isFulltimeSelfUser } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isDemoCohort = DEMO_COHORT_IDS.includes(user?.cohort_id ?? 0);
  const { fullFormattedDate } = useDateParts(unit?.planning_date || "");

  const shouldContentVisible =
    ![null, "NOT_STARTED", undefined].includes(unit?.unit_live_status) ||
    isInstructor ||
    isFulltimeSelfUser;

  return unit ? (
    <main className="relative h-screen flex">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={clsx(
        "w-56 h-full flex flex-col border-r border-white/[0.06] max-lg:bg-surface-1",
        "fixed lg:relative z-30 lg:z-auto transition-transform duration-300",
        sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        {/* Logo + Close */}
        <div className="flex items-center px-5 h-16 shrink-0 relative">
          <Link to="/">
            <img
              src="/c2w_colorful_2.svg"
              alt="Code2Work"
              className="h-7 w-auto"
            />
          </Link>
          <button
            className="lg:hidden absolute right-4 p-1.5 text-white/30 hover:text-white/60 transition-colors cursor-pointer"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={18} />
          </button>
        </div>

        {/* Module info + Nav */}
        <div className="flex-1 overflow-auto px-4 pt-3">
          <div className="px-1 pb-4 mb-3 border-b border-white/[0.06]">
            <span className="text-[11px] font-bold uppercase tracking-wider bg-myGreen/10 text-myGreen px-2 py-0.5 rounded-full">
              Ders
            </span>
            <h2 className="text-base font-semibold mt-1.5 leading-snug text-white">
              {unit.name}
            </h2>
          </div>

          <nav className="select-none space-y-0.5">
            {unit.unit_mls.map((ml, order) => (
              <UnitNavMl
                key={ml.ml_id}
                order={order}
                isActive={order === pin?.mlOrder}
                ml={ml}
              />
            ))}
          </nav>
        </div>

        {/* Next button */}
        {currentPart &&
          canNavigate &&
          shouldContentVisible &&
          !isFulltimeSelfUser && (
            <div className="shrink-0 px-4 pb-4">
              <button
                className="cursor-pointer bg-myGreen hover:bg-myGreen/80 transition-colors text-black py-2.5 px-4 rounded-xl w-full font-semibold text-sm flex items-center justify-center gap-2"
                onClick={() => next()}
              >
                Sonraki kısım
                <ArrowRight size={16} weight="bold" />
              </button>
            </div>
          )}
      </div>

      {/* Content Area */}
      {shouldContentVisible ? (
        <div className="flex-1 contentWrapper min-w-0">
          {/* Mobile toggle */}
          <button
            className="lg:hidden fixed top-3 left-3 z-10 p-2.5 bg-surface-1 border border-white/[0.08] rounded-xl cursor-pointer text-white/50 hover:text-white/80 transition-colors"
            onClick={() => setSidebarOpen(true)}
          >
            <List size={20} />
          </button>
          <UnitCurrentContent>{children}</UnitCurrentContent>
          {isInstructor && (
            <Attendees isLive={isLive} mcc_id={unit.module_cohort_content_id} />
          )}
          {!isInstructor && (
            <NoteButton
              contextType="unit"
              contextId={unit.unit_id}
              label={unit.name}
            />
          )}
        </div>
      ) : (
        <div className="flex-1 items-center justify-center flex px-4">
          <div className="bg-surface-1 border border-white/[0.08] rounded-2xl max-w-sm p-12 pb-10 text-xl/relaxed text-center flex items-center flex-col w-full">
            {isDemoCohort
              ? "Bu ders henüz aktif değil."
              : <>Bu ders eğitmen eşliğinde{" "}
                <span className="text-myGreen">{fullFormattedDate}</span> tarihinde
                yapılacak.</>
            }
          </div>
        </div>
      )}
    </main>
  ) : (
    <LoadingScreen text="Ders yükleniyor..." fullScreen />
  );
}
